import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useProgramListQuery, ProgramListResult } from 'src/hooks/program-list';
import { useSearchParams } from 'react-router-dom';

type ProgramListContextType = {
  data?: ProgramListResult;
  error?: unknown;
  isError: boolean;
  isLoading: boolean;
  refetch: () => void;
};

const ProgramListContext = createContext<ProgramListContextType | undefined>(
  undefined
);

export function ProgramListProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();

  const vendorId = Number(searchParams.get('vendorId') ?? NaN);
  const countryCode = searchParams.get('countryCode');

  const query = useProgramListQuery({
    countryCode: String(countryCode ?? ''),
    vendorIdToSearch: vendorId,
  });

  const value = useMemo(
    () => ({
      data: query.data,
      error: query.error,
      isError: query.isError,
      isLoading: query.isLoading,
      refetch: query.refetch,
    }),
    [query.data, query.isLoading, query.isError, query.error, query.refetch]
  );

  return (
    <ProgramListContext.Provider value={value}>
      {children}
    </ProgramListContext.Provider>
  );
}

export function useProgramListContext() {
  const context = useContext(ProgramListContext);
  if (!context) {
    throw new Error(
      'useProgramListContext must be used within a ProgramListProvider'
    );
  }
  return context;
}
